import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { TrendingUp } from "lucide-react";

const defaultPatientsForCollections = (collections) => {
  return Math.round(collections / 330 / 25) * 25;
};

const ROICalculatorSlide = () => {
  const [collections, setCollections] = useState(100000);
  const [patients, setPatients] = useState(300);
  const patientManuallySet = useRef(false);

  const handleCollectionsChange = (val) => {
    setCollections(val);
    if (!patientManuallySet.current) {
      setPatients(defaultPatientsForCollections(val));
    }
  };

  const handlePatientsChange = (val) => {
    patientManuallySet.current = true;
    setPatients(val);
  };

  const scale = collections / 100000;
  const ptScale = patients / 300;

  const prodFactor = scale * 0.4 + ptScale * 0.6;
  const newPtsLow = Math.max(2, Math.round(3 * prodFactor));
  const newPtsHigh = Math.max(3, Math.round(5 * prodFactor));
  const hygieneRecallsLow = Math.max(2, Math.round(3 * prodFactor));
  const hygieneRecallsHigh = Math.max(3, Math.round(5 * prodFactor));
  const caseAcceptanceLow = Math.max(1, Math.round(1 * prodFactor));
  const caseAcceptanceHigh = Math.max(1, Math.round(2 * prodFactor));
  const prodConservative = Math.round(5000 * scale * (0.6 + ptScale * 0.4));
  const prodRealistic = Math.round(8000 * scale * (0.6 + ptScale * 0.4));

  const ncrConservative = Math.round(collections * 0.01);
  const ncrRealistic = Math.round(collections * 0.02);

  const staffFactor = scale * 0.5 + ptScale * 0.5;
  const fteLow = Math.round(staffFactor * 0.33 * 100) / 100;
  const fteHigh = Math.round(staffFactor * 0.45 * 100) / 100;
  const staffConservative = Math.round(2500 * staffFactor);
  const staffRealistic = Math.round(3500 * staffFactor);

  const softwareScale = Math.max(1, 0.7 + scale * 0.3);
  const softwareConservative = Math.round(2000 * softwareScale);
  const softwareRealistic = Math.round(3000 * softwareScale);

  const totalConservative =
    prodConservative + ncrConservative + staffConservative + softwareConservative;
  const totalRealistic =
    prodRealistic + ncrRealistic + staffRealistic + softwareRealistic;

  const rows = [
    {
      driver: "Additional production from AI agents",
      description:
        "New patients from after-hours booking, hygiene recall & reactivation, case acceptance follow-up, schedule optimization",
      assumptions: `${newPtsLow}-${newPtsHigh} new pts, ${hygieneRecallsLow}-${hygieneRecallsHigh} hygiene recalls, ${caseAcceptanceLow}-${caseAcceptanceHigh} case acceptance conversions/mo`,
      conservative: prodConservative,
      realistic: prodRealistic,
    },
    {
      driver: "Net collection rate improvement",
      description:
        "AI claim scrubbing reduces denials, automated follow-up recovers aging AR, faster EOB posting and payment reconciliation",
      assumptions: `1-2% NCR improvement on $${(collections / 1000).toFixed(0)}K collections`,
      conservative: ncrConservative,
      realistic: ncrRealistic,
    },
    {
      driver: "Staff time savings",
      description:
        "Hours freed from insurance verification, claims, EOB posting, patient comms, recalls, and scheduling",
      assumptions: `${fteLow}-${fteHigh} FTE equivalent time saved across ${patients} patients/mo`,
      conservative: staffConservative,
      realistic: staffRealistic,
    },
    {
      driver: "Software expenses eliminated",
      description:
        "Replaces 8+ point solutions: PMS, comms, marketing, payments, claims, analytics, verification, and more",
      assumptions:
        "Weave, Dental Intel, PMS/server, Zuub, billing portals, & more",
      conservative: softwareConservative,
      realistic: softwareRealistic,
    },
  ];

  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-5 lg:p-6 relative overflow-hidden font-sans">
      {/* Background */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100 rounded-full blur-[120px] opacity-40 translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-50 rounded-full blur-[100px] opacity-40 -translate-x-1/2 translate-y-1/2" />

      <div className="relative z-10 flex flex-col h-full w-full">
        {/* Header */}
        <div className="mb-3 flex-shrink-0">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold mb-1.5 border border-blue-100">
              <TrendingUp className="w-3.5 h-3.5" />
              <span>ROI Potential</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
              ROI potential{" "}
              <span className="text-blue-600">for your practice</span>
            </h2>
            <p className="text-sm text-slate-500 leading-relaxed mt-1 max-w-3xl">
              Asha drives higher production, improves your net collection rate,
              and positions your practice for sustainable growth.
            </p>
          </motion.div>
        </div>

        {/* Sliders */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="flex gap-8 mb-3 flex-shrink-0"
        >
          <div className="flex-1">
            <div className="flex justify-between items-center mb-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Monthly Collections
              </label>
              <span className="text-base font-extrabold text-slate-900 tabular-nums">
                ${collections.toLocaleString()}
              </span>
            </div>
            <input
              type="range"
              min="50000"
              max="500000"
              step="10000"
              value={collections}
              onChange={(e) =>
                handleCollectionsChange(Number(e.target.value))
              }
              className="w-full h-1.5 bg-slate-100 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:shadow-md"
            />
          </div>
          <div className="flex-1">
            <div className="flex justify-between items-center mb-1">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Patients / Month
              </label>
              <span className="text-base font-extrabold text-slate-900 tabular-nums">
                {patients}
              </span>
            </div>
            <input
              type="range"
              min="100"
              max="1000"
              step="25"
              value={patients}
              onChange={(e) =>
                handlePatientsChange(Number(e.target.value))
              }
              className="w-full h-1.5 bg-slate-100 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:shadow-md"
            />
          </div>
        </motion.div>

        {/* Card-style table */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="flex-1 min-h-0 bg-white rounded-2xl border border-slate-200/60 shadow-lg overflow-hidden flex flex-col"
        >
          {/* Header row */}
          <div className="grid grid-cols-[2fr_1.2fr_0.55fr_0.55fr] bg-slate-50 border-b border-slate-200/80 px-5 py-2.5 flex-shrink-0">
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Value Driver</span>
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Assumptions</span>
            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400 text-right">Conservative</span>
            <span className="text-[10px] uppercase tracking-widest font-bold text-blue-500 text-right">Realistic</span>
          </div>

          {/* Data rows */}
          <div className="flex-1 min-h-0">
            {rows.map((row, i) => (
              <div
                key={i}
                className="grid grid-cols-[2fr_1.2fr_0.55fr_0.55fr] px-5 py-3 border-b border-slate-100 hover:bg-slate-50/50 transition-colors items-center"
              >
                <div className="pr-4">
                  <span className="text-sm font-semibold text-slate-900 block">{row.driver}</span>
                  <span className="text-[11px] text-slate-400 leading-snug block mt-0.5">{row.description}</span>
                </div>
                <span className="text-[11px] text-slate-500 leading-snug pr-3">{row.assumptions}</span>
                <span className="text-sm font-bold text-slate-700 text-right tabular-nums">
                  ${row.conservative.toLocaleString()}
                </span>
                <span className="text-sm font-extrabold text-blue-600 text-right tabular-nums">
                  ${row.realistic.toLocaleString()}
                </span>
              </div>
            ))}
          </div>

          {/* Total Row */}
          <div className="grid grid-cols-[2fr_1.2fr_0.55fr_0.55fr] bg-[#0f172a] px-5 py-3.5 items-center flex-shrink-0">
            <span className="text-sm font-extrabold text-white tracking-wide col-span-2">
              Total Estimated Monthly ROI
            </span>
            <span className="text-lg font-extrabold text-white text-right tabular-nums">
              ${totalConservative.toLocaleString()}
            </span>
            <span className="text-lg font-extrabold text-blue-400 text-right tabular-nums">
              ${totalRealistic.toLocaleString()}
            </span>
          </div>
        </motion.div>

        <p className="text-[9px] text-slate-400 mt-2 flex-shrink-0 leading-relaxed">
          Disclaimer: These figures are illustrative estimates only and do not
          constitute a guarantee of results. Actual outcomes vary based on
          practice size, patient mix, payer contracts, staff engagement, and
          other factors.
        </p>
      </div>
    </div>
  );
};

export default ROICalculatorSlide;
